import React, { forwardRef, useRef, useImperativeHandle, useEffect } from 'react';

export default forwardRef(function GetPaginaTrece(props, ref) {
  const { logoSrc } = props || {};
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);

  // drawWrappedText: draws wrapped text and returns last Y
  const drawWrappedText = (ctx, text, x, y, maxWidth, lineHeight) => {
    if (!text) return y;
    const paragraphs = String(text).split(/\r?\n/);
    let curY = y;
    for (let p = 0; p < paragraphs.length; p++) {
      const words = paragraphs[p].split(/\s+/);
      let line = '';
      for (let n = 0; n < words.length; n++) {
        const testLine = line ? (line + ' ' + words[n]) : words[n];
        const metrics = ctx.measureText(testLine);
        if (metrics.width > maxWidth && line) {
          ctx.fillText(line, x, curY);
          line = words[n];
          curY += lineHeight;
        } else {
          line = testLine;
        }
      }
      if (line) {
        ctx.fillText(line, x, curY);
        curY += lineHeight;
      }
      // preserve a blank line gap between paragraphs
      if (p < paragraphs.length - 1) curY += Math.round(lineHeight * 0.25);
    }
    return curY;
  };

  const drawJustifiedText = (ctx, text, x, y, maxWidth, lineHeight) => {
    if (!text) return y;
    const paragraphs = String(text).split(/\r?\n/);
    let curY = y;
    const spaceW = ctx.measureText(' ').width;
    for (let p = 0; p < paragraphs.length; p++) {
      const words = paragraphs[p].split(/\s+/).filter(Boolean);
      let line = [];
      let lineW = 0;
      for (let i = 0; i < words.length; i++) {
        const w = words[i];
        const wW = ctx.measureText(w).width;
        if (line.length === 0) {
          line = [w];
          lineW = wW;
        } else if (lineW + spaceW + wW <= maxWidth) {
          line.push(w);
          lineW += spaceW + wW;
        } else {
          if (line.length > 1) {
            const gaps = line.length - 1;
            const extra = (maxWidth - lineW) / gaps;
            let cx = x;
            for (let j = 0; j < line.length; j++) {
              ctx.fillText(line[j], cx, curY);
              cx += ctx.measureText(line[j]).width + spaceW + extra;
            }
          } else {
            ctx.fillText(line[0], x, curY);
          }
          curY += lineHeight;
          line = [w];
          lineW = wW;
        }
      }
      if (line.length) {
        ctx.fillText(line.join(' '), x, curY);
        curY += lineHeight;
      }
      if (p < paragraphs.length - 1) curY += Math.round(lineHeight * 0.25);
    }
    return curY;
  };

  const draw = (opts = {}) => {
    return new Promise((resolve) => {
      const canvas = canvasRef.current;
      const wrapper = wrapperRef.current;
      if (!canvas) return resolve();

      const containerWidth = wrapper ? wrapper.clientWidth : 800;
      const width = Math.max(600, containerWidth);
      const height = Math.round(width * (8.5 / 11));
      const dpr = window.devicePixelRatio || 1;

      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      const ctx = canvas.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, width, height);

      // background
      ctx.fillStyle = opts.bgColor || props.bgColor || '#ffffff';
      ctx.fillRect(0, 0, width, height);

      const padding = 28;
      let y = padding;

      // left title square
      const titleFontSize = opts.titleFontSize || 48;
      const squareSize = typeof opts.titleSquareSize === 'number' ? opts.titleSquareSize : Math.round(titleFontSize * 1.5);
      const squareGap = typeof opts.titleSquareGap === 'number' ? opts.titleSquareGap : 22;
      const squareText = opts.titleSquareText || '4';

      ctx.fillStyle = opts.titleSquareBg || '#f5cb13ff';
      ctx.fillRect(padding, y, squareSize, squareSize);

      ctx.fillStyle = opts.titleSquareColor || '#ffffffff';
      ctx.font = `bold ${Math.round(squareSize * 0.55)}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(squareText, padding + squareSize / 2, y + squareSize / 2);

      // title (to the right of square)
      const title = opts.title || props.title || 'Glosario';
      const titleX = padding + squareSize + squareGap;
      const titleMaxW = Math.min(typeof opts.titleMaxWidth === 'number' ? opts.titleMaxWidth : 900, width - titleX - padding);
      ctx.fillStyle = opts.titleColor || '#111827';
      ctx.font = `bold ${titleFontSize}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';

      // draw title with wrapping
      const titleLineHeight = Math.round(titleFontSize * 1.05);
      y = drawWrappedText(ctx, title, titleX, y, titleMaxW, titleLineHeight);

      // gap after title
      y += 100;

      // draw logo (top-right) if provided
      const mainLogoSrc = opts.logoSrc || props.logoSrc;
      if (mainLogoSrc) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          try {
            const maxW = Math.min(160, Math.round(width * 0.11));
            const w = Math.min(maxW, img.width);
            const h = (img.height / img.width) * w;
            const lx = width - padding - w;
            const ly = padding;
            ctx.drawImage(img, lx, ly, w, h);
          } catch (e) { /* ignore */ }
        };
        img.onerror = () => { /* ignore */ };
        img.src = mainLogoSrc;
      }

      // glossary list
      const glosarioData = opts.glosarioData || props.glosarioData || [];
      if (Array.isArray(glosarioData) && glosarioData.length) {
        const termFontSize = opts.termFontSize || 30;
        const defFontSize = opts.defFontSize || 30;
        const termLineH = Math.round(termFontSize * 1.4);
        const defLineH = Math.round(defFontSize * 1.5);

        const contentX = padding;
        const contentW = width - padding * 2;

        for (let i = 0; i < glosarioData.length; i++) {
          const item = glosarioData[i] || {};
          const termino = String(item.termino || '').trim();
          const definicion = String(item.definicion || '').trim();

          // draw term on its own line (bold)
          ctx.fillStyle = opts.termColor || '#0f172a';
          ctx.font = `600 ${termFontSize}px Inter, Arial, sans-serif`;
          ctx.textAlign = 'left';
          ctx.textBaseline = 'top';
          ctx.fillText(termino, contentX, y);

          // draw definition as a paragraph below the term (left-aligned, wrapped)
          ctx.fillStyle = opts.defColor || '#111827';
          ctx.font = `${defFontSize}px Inter, Arial, sans-serif`;
          const defStartY = y + termLineH;
          const defEndY = drawJustifiedText(ctx, definicion, contentX, defStartY, contentW, defLineH);

          // advance y for next entry with spacing
          y = defEndY + 12;

          // stop if reaching bottom (single page)
          if (y + 60 > height - padding) break;
        }
      } else {
        // no data message
        ctx.fillStyle = '#6b7280';
        ctx.font = `16px Inter, Arial, sans-serif`;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        ctx.fillText('No hay entradas de glosario para mostrar.', padding, y);
      }

      // resolve when drawing finished (logo may still be loading but that's ok)
      resolve({ canvas, width, height });
    });
  };

  const download = (filename) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = filename || `pagina_trece_${new Date().toISOString().slice(0,10)}.png`;
    a.click();
  };

  useImperativeHandle(ref, () => ({
    draw,
    download,
    getDataUrl: () => {
      const canvas = canvasRef.current;
      return canvas ? canvas.toDataURL('image/png') : null;
    }
  }), [draw]);

  useEffect(() => {
    draw();
    const onResize = () => draw();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={wrapperRef} style={{ width: '100%' }}>
      <canvas ref={canvasRef} style={{ width: '100%', display: 'block', borderRadius: 6 }} />
    </div>
  );
});